"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Logger = void 0;
const client_cloudwatch_1 = require("@aws-sdk/client-cloudwatch");
const structures_1 = require("@krauters/structures");
const chalk_1 = __importDefault(require("chalk"));
const uuid_1 = require("uuid");
const winston_1 = require("winston");
const config_1 = require("./config");
const structures_2 = require("./structures");
class Logger {
    static instance;
    cloudwatch;
    config;
    logger;
    metadata = {};
    colors;
    levels;
    constructor(options = {}) {
        const { configOptions, context, format: customFormat, transports: customTransports } = options;
        this.config = (0, config_1.getConfig)(configOptions);
        const requestId = this.getRequestId(context);
        this.metadata = this.getBaseMetadata(requestId);
        this.levels = { ...winston_1.config.npm.levels, trace: 8 };
        this.colors = { ...winston_1.config.npm.colors, trace: 'magenta' };
        (0, winston_1.addColors)(this.colors);
        this.logger = (0, winston_1.createLogger)({
            format: customFormat ?? this.getFormatter(),
            level: this.config.LOG_LEVEL,
            levels: this.levels,
            transports: customTransports ?? [new winston_1.transports.Console()],
        });
        this.cloudwatch = new client_cloudwatch_1.CloudWatchClient({});
    }
    static getInstance(options) {
        if (!Logger.instance) {
            Logger.instance = new Logger(options);
        }
        else if (options) {
            Logger.instance.updateInstance(options);
        }
        return Logger.instance;
    }
    addToAllLogs(key, value) {
        this.metadata[key] = value;
    }
    debug(message, data) {
        return this.log({ level: structures_2.LogLevel.Debug, message, metadata: data });
    }
    error(message, data) {
        return this.log({ level: structures_2.LogLevel.Error, message, metadata: data });
    }
    formatLogMessage(info, separator, prefix) {
        const { level, message, timestamp, ...rest } = info;
        return [
            chalk_1.default.blue(String(timestamp)),
            String(level),
            prefix + String(message),
            ...Object.entries(rest)
                .filter(([, value]) => value !== undefined && value !== '')
                .map(([key, value]) => `${key}: ${String(value)}`),
        ].join(separator);
    }
    getFormatter() {
        const formatters = {
            friendly: this.getFriendlyFormat.bind(this),
            structured: this.getStructuredFormat.bind(this),
        };
        const formatType = this.config.LOG_FORMAT;
        if (!(formatType in formatters)) {
            throw new Error(`Invalid LOG_FORMAT [${this.config.LOG_FORMAT}] provided by config.`);
        }
        return formatters[formatType]();
    }
    getFriendlyFormat() {
        const separator = this.config.LOG_SECTION_SEPARATOR;
        const prefix = this.config.LOG_PREFIX ?? '';
        return winston_1.format.combine(winston_1.format.colorize({ all: true }), winston_1.format.timestamp({ format: this.config.TIMESTAMP_FORMAT }), winston_1.format.printf((info) => this.formatLogMessage(this.getLogObject({ fieldsToHide: this.config.LOG_FRIENDLY_FIELDS_HIDE, info }), separator, prefix)));
    }
    getLogObject({ fieldsToHide = [], info }) {
        const combined = { ...this.metadata, ...info };
        for (const field of fieldsToHide) {
            delete combined[field];
        }
        return combined;
    }
    getRequestId(context) {
        if (this.config.REQUEST_ID)
            return this.config.REQUEST_ID;
        if (context?.awsRequestId)
            return context.awsRequestId;
        if (this.config.SIMPLE_LOGS)
            return (0, uuid_1.v4)().split('-')[0];
        return (0, uuid_1.v4)();
    }
    getStructuredFormat() {
        return winston_1.format.combine(winston_1.format.timestamp({ format: this.config.TIMESTAMP_FORMAT }), winston_1.format.printf((info) => JSON.stringify(this.getLogObject({ fieldsToHide: this.config.LOG_STRUCTURED_FIELDS_HIDE, info }))));
    }
    info(message, data) {
        return this.log({ level: structures_2.LogLevel.Info, message, metadata: data });
    }
    log(options) {
        const { level, message, metadata } = options;
        const combinedMeta = { ...this.metadata, ...(metadata ?? {}) };
        this.logger.log(level, message, combinedMeta);
        return message;
    }
    async publishMetric(options) {
        const { dimensions = [], metricName, unit = client_cloudwatch_1.StandardUnit.Count, value } = options;
        const params = {
            MetricData: [
                {
                    Dimensions: dimensions,
                    MetricName: metricName,
                    Timestamp: new Date(),
                    Unit: unit,
                    Value: value,
                },
            ],
            Namespace: this.config.CODENAME,
        };
        try {
            await this.cloudwatch.send(new client_cloudwatch_1.PutMetricDataCommand(params));
            this.info(`Metric published [${metricName}]`);
        }
        catch (error) {
            this.error(`Failed to publish metric [${metricName}]`, { error, metricName });
        }
    }
    removeFromAllLogs(...keys) {
        const flattenedKeys = keys.flat();
        for (const key of flattenedKeys) {
            delete this.metadata[key];
        }
    }
    trace(message, data) {
        this.log({ level: structures_2.LogLevel.Trace, message, metadata: data });
    }
    updateInstance(options) {
        const { configOptions, context, format: customFormat, transports: customTransports } = options;
        if (configOptions) {
            this.config = (0, config_1.getConfig)(configOptions);
            const newRequestId = this.getRequestId(context);
            const userFields = { ...this.metadata };
            for (const key of Object.keys(this.getBaseMetadata(newRequestId))) {
                delete userFields[key];
            }
            this.metadata = { ...this.getBaseMetadata(newRequestId), ...userFields };
            this.logger.format = this.getFormatter();
            this.logger.level = this.config.LOG_LEVEL;
        }
        else if (context) {
            const newRequestId = this.getRequestId(context);
            const userFields = { ...this.metadata };
            for (const key of Object.keys(this.getBaseMetadata(newRequestId))) {
                delete userFields[key];
            }
            this.metadata = { ...this.getBaseMetadata(newRequestId), ...userFields };
            this.logger.format = this.getFormatter();
        }
        if (customFormat) {
            this.logger.format = customFormat;
        }
        if (customTransports) {
            this.logger.clear();
            for (const transport of customTransports) {
                this.logger.add(transport);
            }
        }
    }
    updateLevels(newLevels, newColors) {
        this.levels = { ...this.levels, ...newLevels };
        this.colors = { ...this.colors, ...newColors };
        (0, winston_1.addColors)(this.colors);
        const existingTransports = this.logger.transports.map((transport) => transport);
        this.logger.clear();
        this.logger = (0, winston_1.createLogger)({
            format: this.getFormatter(),
            level: this.config.LOG_LEVEL,
            levels: this.levels,
            transports: existingTransports,
        });
        this.info('Logger levels and colors have been updated.');
    }
    warn(message, data) {
        this.log({ level: structures_2.LogLevel.Warn, message, metadata: data });
    }
    getBaseMetadata(requestId) {
        const base = {
            codename: this.config.CODENAME,
            env: this.config.ENV,
            host: this.config.HOST,
            package: this.config.PACKAGE,
            requestId: requestId,
            stage: this.config.STAGE,
            version: this.config.VERSION,
        };
        return Object.fromEntries(Object.entries(base).filter(([, value]) => value !== structures_2.empty && value !== structures_1.Env.Unknown));
    }
}
exports.Logger = Logger;
//# sourceMappingURL=logger.js.map